// components/journey-surface/journey-header.js
// ════════════════════════════════════════════════
// Header rows for the expanded journey map panel:
//   Row 1 — title + node count + close button
//   Row 2 — lens toggles + search input/icon
//   Row 3 — lens description (hover reveal)
//   Row 4 — map toggle row (collapsible SVG canvas)

import React from 'react';
import { LENSES, LensToggle } from './journey-lenses';

export function JourneyHeader({
  nodes,
  activeLenses,
  hoveredLens,
  setHoveredLens,
  searchOpen,
  setSearchOpen,
  searchQuery,
  setSearchQuery,
  searchInputRef,
  searchWrapperRef,
  mapOpen,
  setMapOpen,
  focusedNodeId,
  onToggle,
  toggleLens,
}) {
  return (
    <>
      {/* Row 1: title + node count + close */}
      <div style={{
        height: 32,
        background: '#07070a',
        borderBottom: '1px solid #1e1e2a',
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        gap: 6,
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 10, color: '#555', fontFamily: 'Georgia, serif', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Journey
        </span>
        <span style={{
          fontSize: 9, padding: '1px 7px', borderRadius: 10,
          border: '1px solid rgba(122,158,130,0.25)',
          background: 'rgba(122,158,130,0.08)', color: '#7a9e82',
          letterSpacing: '0.06em', fontFamily: 'Georgia, serif',
        }}>
          {(nodes || []).length}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onToggle}
          title="Close journey map"
          style={{ background: 'none', border: '1px solid #1e1e2a', borderRadius: 4, cursor: 'pointer', color: '#555', fontSize: 9, padding: '2px 8px', fontFamily: 'Georgia, serif' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.color = '#888'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e1e2a'; e.currentTarget.style.color = '#555'; }}
        >
          Close
        </button>
      </div>

      {/* Row 2: lens toggles + search */}
      <div ref={searchWrapperRef} style={{
        display: 'flex', alignItems: 'center', gap: 3,
        padding: '4px 10px', borderBottom: '1px solid #1e1e2a', flexShrink: 0,
        position: 'relative',
      }}>
        {LENSES.map(lens => (
          <LensToggle
            key={lens.id}
            lens={lens}
            active={activeLenses.has(lens.id)}
            onClick={() => toggleLens(lens.id)}
            onHover={setHoveredLens}
          />
        ))}
        <div style={{ flex: 1 }} />
        {searchOpen ? (
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoFocus
            onKeyDown={e => { if (e.key === 'Escape') { setSearchOpen(false); setSearchQuery(''); } }}
            style={{
              width: 120, background: '#141414', border: '1px solid #1e1e2a',
              borderRadius: 4, padding: '3px 7px', fontSize: 10,
              fontFamily: 'Georgia, serif', color: '#d4d4d4', outline: 'none',
              transition: 'width 0.2s',
            }}
          />
        ) : (
          <button
            onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
            title="Search nodes"
            style={{
              background: 'none', border: '1px solid transparent',
              borderRadius: 4, cursor: 'pointer', color: '#555',
              fontSize: 11, padding: '2px 4px', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#c8a96e'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#555'; }}
          >
            ⌕
          </button>
        )}
      </div>

      {/* Lens description bar — appears on hover, inline in flow */}
      {hoveredLens && (
        <div style={{ padding: '4px 10px', borderBottom: '1px solid #1e1e2a', background: '#07070a', flexShrink: 0 }}>
          <span style={{ fontSize: 9, color: '#c8a96e', fontFamily: 'Georgia, serif', fontWeight: 700 }}>
            {hoveredLens.label}
          </span>
          <span style={{ color: '#333', margin: '0 5px' }}>—</span>
          <span style={{ fontSize: 9, color: '#666', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            {hoveredLens.description}
          </span>
        </div>
      )}

      {/* Map toggle row */}
      <div
        onClick={() => setMapOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '7px 10px', cursor: 'pointer',
          background: mapOpen ? '#07070a' : '#07070a',
          borderBottom: '1px solid #1e1e2a',
          transition: 'background 0.15s', flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, color: '#555', fontFamily: 'Georgia, serif', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Map view
          </span>
          <span style={{ fontSize: 9, color: '#333', background: '#111', border: '1px solid #1e1e2a', borderRadius: 10, padding: '1px 6px', fontFamily: 'Georgia, serif' }}>
            {(nodes || []).length} nodes · {(nodes || []).filter(n => n.status === 'active' || n.status === 'building' || n.id === focusedNodeId).length} active
          </span>
        </div>
        <span style={{
          fontSize: 10, color: '#333',
          transform: mapOpen ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 0.22s', display: 'inline-block',
        }}>
          ▶
        </span>
      </div>
    </>
  );
}
